import React from "react";
import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris/property-filter/interfaces";
import {
  PropertyFilterOperator,
  PropertyFilterOption,
} from "@amzn/awsui-collection-hooks/cjs/interfaces";
import { FormField } from "@amzn/awsui-components-react";
import { DateRangeField } from "../searchComponents";
import { Range } from "../searchSideBarConnector";
import { serviceToDisplay } from "../../../util/dateUtils";
import { Marketplaces } from "../../../constants/common.ts";
import {
  ASIN_CATALOG_LIVENESS_STATUSES,
  ASIN_PARTIES,
  LAUNCH_ASIN_TYPES,
  WEBLAB_STATUS,
  BUYBOX_STATUS,
  ITEM_TYPES,
  SOFTWARE_OOBE,
} from "../../../constants/launchAsinTypes";
import {
  BASELINE_STATUS_ITEMS_COMPLIANT,
  BASELINE_STATUS_ITEMS_NON_COMPLIANT,
  VALIDATION_STATUS_ITEMS_FAIL,
  VALIDATION_STATUS_ITEMS_PASS,
  VALIDATION_STATUS_ITEMS_PASS_EXCEPTION,
  sortOptionsByLabelAlphabetical,
  toMultiSelectOption,
} from "../SearchSideBar-config";

// Overall validation status is stored on DACS as `PASSED` /
// `FAILED` value. Documents missing all eight source attributes leave
// the field absent (rendered as No Data on the bar), so `NO_DATA` is
// not offered as a filter value here.
const VALIDATION_OVERALL_STATUS_LABELS: { readonly [key: string]: string } = {
  PASSED: "Passed",
  FAILED: "Failed",
};

// Re-exported so existing imports (`SearchPropertyFilter-config`)
// keep working. The canonical definition lives in
// `./multiSelectPropertyKeys` so parser code and its tests can pull
// it in without dragging Cloudscape along.
export { MULTISELECT_PROPERTY_KEYS } from "./multiSelectPropertyKeys";

/**
 * Build a Cloudscape enum-token `format` function that maps each raw
 * value through `labelFor` and joins them with ", ". Handles nullish
 * and non-array inputs defensively — Polaris can call `format` mid-
 * edit before the value is finalised.
 */
const formatEnumValues =
  (labelFor: (v: string) => string) =>
  (value: string[] | string | null | undefined): string => {
    if (Array.isArray(value)) {
      return value.map((v) => labelFor(v)).join(", ");
    }
    if (value === null || value === undefined || value === "") return "";
    return labelFor(String(value));
  };

export const rangeFormatter = (input: Range) => {
  if (!input?.lower || !input?.upper) return "";
  const lowerDate = serviceToDisplay(input.lower);
  const upperDate = serviceToDisplay(input.upper);
  return `${lowerDate} - ${upperDate}`;
};

export const dateRangeWidgetWrapper = (
  attributeName: string,
  value: Range,
  onChange: (value: null | any) => void
) => {
  return (
    <FormField>
      {DateRangeField(
        attributeName,
        (res: any) => {
          const value = res?.payload?.queryState?.value;
          if (value && "lower" in value && "upper" in value) {
            const { lower, upper } = value;
            onChange({
              lower: lower.toISOString(),
              upper: upper.toISOString(),
            });
          } else {
            onChange(null);
          }
        },
        value as Range
      )}
    </FormField>
  );
};

export const propertyFiltersConfig = (
  productLinesData: any,
  programNameData: any
): {
  [propertyKey: string]: {
    filteringProperty: PropertyFilterProps.FilteringProperty;
    filteringOptions?: PropertyFilterOption[];
  };
} => {
  return {
    // Free-text ASIN filter — one ASIN or a comma-separated list per
    // token value, no `filteringOptions` (ASINs aren't a fixed enum).
    asin: {
      filteringProperty: {
        key: "asin",
        operators: ["="],
        propertyLabel: "ASIN",
        groupValuesLabel: "ASIN values",
      },
    },
    announceDate: {
      filteringProperty: {
        key: "announceDate",
        operators: ["="].map((operator: PropertyFilterOperator) => ({
          operator,
          form: ({ value, onChange }: any) =>
            dateRangeWidgetWrapper("announceDate", value, onChange),
          format: rangeFormatter,
        })),
        propertyLabel: "Announce Date",
        groupValuesLabel: "Announce Date values",
      },
    },
    onlineStreetDate: {
      filteringProperty: {
        key: "onlineStreetDate",
        operators: ["="].map((operator: any) => ({
          operator,
          form: ({ value, onChange }: any) =>
            dateRangeWidgetWrapper("onlineStreetDate", value, onChange),
          format: rangeFormatter,
        })),
        propertyLabel: "Online Street Date",
        groupValuesLabel: "Online Street Date values",
      },
    },
    programId: {
      filteringProperty: {
        key: "programId",
        operators: ["="].map((operator) => ({
          operator,
          format: (programId: string) => {
            const program = programNameData.values.find(
              (program: any) => program.id === programId
            );
            return program?.name || programId;
          },
        })),
        propertyLabel: "Program",
        groupValuesLabel: "Program values",
      },
      filteringOptions: sortOptionsByLabelAlphabetical(
        programNameData?.values?.map((program: any) =>
          toMultiSelectOption(program.id, program.name)
        ) ?? []
      ).map(
        (option): PropertyFilterOption => ({
          propertyKey: "programId",
          value: option.value ?? "",
          label: option.label ?? "",
        })
      ),
    },
    // Multi-select enum property: renders a Cloudscape checkbox
    // popover with Cancel/Apply. Token value is `string[]`.
    country: {
      filteringProperty: {
        key: "country",
        operators: [
          {
            operator: "=",
            tokenType: "enum" as const,
            format: formatEnumValues((v) => v),
          },
        ],
        propertyLabel: "Marketplace",
        groupValuesLabel: "Marketplace values",
      },
      filteringOptions: Marketplaces.map(
        (country): PropertyFilterOption => ({
          propertyKey: "country",
          value: country,
          label: country,
        })
      ),
    },
    // Multi-select enum property: renders a Cloudscape checkbox
    // popover with Cancel/Apply. Token value is `string[]`.
    asinParty: {
      filteringProperty: {
        key: "asinParty",
        operators: [
          {
            operator: "=",
            tokenType: "enum" as const,
            format: formatEnumValues((v) => v),
          },
        ],
        propertyLabel: "ASIN Partiness",
        groupValuesLabel: "ASIN Partiness values",
      },
      filteringOptions: ASIN_PARTIES.map(
        (asinParty): PropertyFilterOption => ({
          propertyKey: "asinParty",
          value: asinParty,
          label: asinParty,
        })
      ),
    },
    // Multi-select enum property: renders a Cloudscape checkbox
    // popover with Cancel/Apply. Token value is `string[]`; the
    // display formatter turns each service-side key into its
    // human-readable name before joining.
    asinType: {
      filteringProperty: {
        key: "asinType",
        operators: [
          {
            operator: "=",
            tokenType: "enum" as const,
            format: formatEnumValues((v) => LAUNCH_ASIN_TYPES[v] || v),
          },
        ],
        propertyLabel: "ASIN type",
        groupValuesLabel: "ASIN type values",
      },
      filteringOptions: Object.entries(LAUNCH_ASIN_TYPES).map(
        ([asinTypeKey, readableName]): PropertyFilterOption => ({
          propertyKey: "asinType",
          value: asinTypeKey,
          label: readableName,
        })
      ),
    },
    baselineStatus: {
      filteringProperty: {
        key: "baselineStatus",
        operators: ["="].map((operator) => ({
          operator,
          format: (value: string) => {
            if (BASELINE_STATUS_ITEMS_COMPLIANT[value])
              return BASELINE_STATUS_ITEMS_COMPLIANT[value];
            if (BASELINE_STATUS_ITEMS_NON_COMPLIANT[value])
              return BASELINE_STATUS_ITEMS_NON_COMPLIANT[value];
            return value;
          },
        })),
        propertyLabel: "Baseline Status",
        groupValuesLabel: "Baseline Status values",
      },
      filteringOptions: Object.entries({
        ...BASELINE_STATUS_ITEMS_COMPLIANT,
        ...BASELINE_STATUS_ITEMS_NON_COMPLIANT,
      }).map(
        ([key, label]): PropertyFilterOption => ({
          propertyKey: "baselineStatus",
          value: key,
          label: label,
        })
      ),
    },
    // Default to `=` (exact keyword match) so a phrase like "Extended
    // Warranty Parent" doesn't tokenize and pull in unrelated
    // descriptions sharing a word (LEAP-22758). `:` (Contains) is
    // still offered for opt-in word-level match.
    userDescription: {
      filteringProperty: {
        key: "userDescription",
        operators: ["=", ":"],
        defaultOperator: "=",
        propertyLabel: "User Description",
        groupValuesLabel: "User Description values",
      },
    },
    description: {
      filteringProperty: {
        key: "description",
        operators: ["=", ":"],
        defaultOperator: "=",
        propertyLabel: "Description",
        groupValuesLabel: "Description values",
      },
    },
    partNumber: {
      filteringProperty: {
        key: "partNumber",
        operators: ["="],
        propertyLabel: "Part Number",
        groupValuesLabel: "Part Number values",
      },
    },
    productLineId: {
      filteringProperty: {
        key: "productLineId",
        operators: ["="].map((operator) => ({
          operator,
          format: (productLineId: string) => {
            const productLine = productLinesData.values.find(
              (productLine: any) => productLine.id === productLineId
            );
            return productLine?.name || productLineId;
          },
        })),
        propertyLabel: "Product Line",
        groupValuesLabel: "Product Line values",
      },
      filteringOptions: sortOptionsByLabelAlphabetical(
        productLinesData?.values?.map((productLine: any) =>
          toMultiSelectOption(productLine.id, productLine.name)
        ) ?? []
      ).map(
        (option): PropertyFilterOption => ({
          propertyKey: "productLineId",
          value: option.value ?? "",
          label: option.label ?? "",
        })
      ),
    },
    bundleBaseComponent: {
      filteringProperty: {
        key: "bundleBaseComponent",
        operators: ["="],
        propertyLabel: "Bundle Base Component",
        groupValuesLabel: "Bundle Base Component values",
      },
    },
    catalogLiveness: {
      filteringProperty: {
        key: "catalogLiveness",
        operators: ["="],
        propertyLabel: "Catalog Liveness",
        groupValuesLabel: "Catalog Liveness values",
      },
      filteringOptions: ASIN_CATALOG_LIVENESS_STATUSES.map(
        (catalogLiveness): PropertyFilterOption => ({
          propertyKey: "catalogLiveness",
          value: catalogLiveness ?? "",
          label: catalogLiveness ?? "",
        })
      ),
    },
    liveness: {
      filteringProperty: {
        key: "liveness",
        operators: ["="],
        propertyLabel: "Liveness",
        groupValuesLabel: "Liveness values",
      },
      filteringOptions: ASIN_CATALOG_LIVENESS_STATUSES.map(
        (catalogLiveness): PropertyFilterOption => ({
          propertyKey: "liveness",
          value: catalogLiveness ?? "",
          label: catalogLiveness ?? "",
        })
      ),
    },
    bundleComponents: {
      filteringProperty: {
        key: "bundleComponents",
        operators: ["="],
        propertyLabel: "Bundle Component",
        groupValuesLabel: "Bundle Component values",
      },
    },
    itemType: {
      filteringProperty: {
        key: "itemType",
        operators: ["="].map((operator) => ({
          operator,
          format: (v: string) => ITEM_TYPES[v] || v,
        })),
        propertyLabel: "Item Type",
        groupValuesLabel: "Item Type values",
      },
      filteringOptions: Object.entries(ITEM_TYPES).map(
        ([key, readableName]): PropertyFilterOption => ({
          propertyKey: "itemType",
          value: key,
          label: readableName,
        })
      ),
    },
    salesChannel: {
      filteringProperty: {
        key: "salesChannel",
        operators: [":"],
        defaultOperator: ":",
        propertyLabel: "Sales Channel",
        groupValuesLabel: "Sales Channel values",
      },
    },
    upc: {
      filteringProperty: {
        key: "upc",
        operators: [":"],
        defaultOperator: ":",
        propertyLabel: "UPC",
        groupValuesLabel: "UPC values",
      },
    },
    dtcp: {
      filteringProperty: {
        key: "dtcp",
        operators: [":"],
        defaultOperator: ":",
        propertyLabel: "DTCP",
        groupValuesLabel: "DTCP values",
      },
    },
    productionFamily: {
      filteringProperty: {
        key: "productionFamily",
        operators: [":"],
        defaultOperator: ":",
        propertyLabel: "Production Family",
        groupValuesLabel: "Production Family values",
      },
    },
    gtin: {
      filteringProperty: {
        key: "gtin",
        operators: [":"],
        defaultOperator: ":",
        propertyLabel: "GTIN",
        groupValuesLabel: "GTIN values",
      },
    },
    allowListedDeviceCode: {
      filteringProperty: {
        key: "allowListedDeviceCode",
        operators: ["="],
        defaultOperator: "=",
        propertyLabel: "Allow Listed Device Code",
        groupValuesLabel: "Allow Listed Device Code values",
      },
    },
    allowListedDtid: {
      filteringProperty: {
        key: "allowListedDtid",
        operators: ["="],
        defaultOperator: "=",
        propertyLabel: "Allow Listed DTID",
        groupValuesLabel: "Allow Listed DTID values",
      },
    },
    bomDtids: {
      filteringProperty: {
        key: "bomDtids",
        operators: ["="],
        defaultOperator: "=",
        propertyLabel: "BOM DTID",
        groupValuesLabel: "BOM DTID values",
      },
    },
    // Scalar overall status. Complements the per-pipeline
    // `validationStatus` filter below.
    validationOverallStatus: {
      filteringProperty: {
        key: "validationOverallStatus",
        operators: ["="].map((operator) => ({
          operator,
          format: (value: string) =>
            VALIDATION_OVERALL_STATUS_LABELS[value] ?? value,
        })),
        propertyLabel: "Overall Validation Status",
        groupValuesLabel: "Overall Validation Status values",
      },
      filteringOptions: Object.entries(VALIDATION_OVERALL_STATUS_LABELS).map(
        ([key, label]): PropertyFilterOption => ({
          propertyKey: "validationOverallStatus",
          value: key,
          label,
        })
      ),
    },
    validationStatus: {
      filteringProperty: {
        key: "validationStatus",
        operators: ["="].map((operator) => ({
          operator,
          format: (value: string) => {
            if (VALIDATION_STATUS_ITEMS_FAIL[value])
              return VALIDATION_STATUS_ITEMS_FAIL[value];
            if (VALIDATION_STATUS_ITEMS_PASS[value])
              return VALIDATION_STATUS_ITEMS_PASS[value];
            if (VALIDATION_STATUS_ITEMS_PASS_EXCEPTION[value])
              return VALIDATION_STATUS_ITEMS_PASS_EXCEPTION[value];
            return value;
          },
        })),
        propertyLabel: "Validation Status",
        groupValuesLabel: "Validation Status values",
      },
      filteringOptions: Object.entries({
        ...VALIDATION_STATUS_ITEMS_FAIL,
        ...VALIDATION_STATUS_ITEMS_PASS,
        ...VALIDATION_STATUS_ITEMS_PASS_EXCEPTION,
      }).map(
        ([key, label]): PropertyFilterOption => ({
          propertyKey: "validationStatus",
          value: key ?? "",
          label: label ?? "",
        })
      ),
    },
    domainName: {
      filteringProperty: {
        key: "domainName",
        operators: ["="],
        defaultOperator: "=",
        propertyLabel: "Domain Name",
        groupValuesLabel: "Domain Name values",
      },
    },
    makoWeblabStatus: {
      filteringProperty: {
        key: "makoWeblabStatus",
        operators: ["="],
        propertyLabel: "Weblab Status",
        groupValuesLabel: "Weblab Status values",
      },
      filteringOptions: WEBLAB_STATUS.map(
        (status): PropertyFilterOption => ({
          propertyKey: "makoWeblabStatus",
          value: status,
          label: status,
        })
      ),
    },
    buyBoxStatus: {
      filteringProperty: {
        key: "buyBoxStatus",
        operators: ["="],
        propertyLabel: "BuyBox Status",
        groupValuesLabel: "BuyBox Status values",
      },
      filteringOptions: BUYBOX_STATUS.map(
        (status): PropertyFilterOption => ({
          propertyKey: "buyBoxStatus",
          value: status,
          label: status,
        })
      ),
    },
    softwareOutOfBoxExperience: {
      filteringOptions: SOFTWARE_OOBE.map(
        (oobe): PropertyFilterOption => ({
          propertyKey: "softwareOutOfBoxExperience",
          value: oobe.filterValue,
          label: oobe.label,
        })
      ),
      filteringProperty: {
        key: "softwareOutOfBoxExperience",
        operators: [":"],
        defaultOperator: ":",
        propertyLabel: "Software Out Of Box Experience",
        groupValuesLabel: "Software Out Of Box Experience values",
      },
    },
  };
};

export const PROPERTY_FILTER_I18N: PropertyFilterProps.I18nStrings = {
  cancelActionText: "Cancel",
  dismissAriaLabel: "Dismiss",
  editTokenHeader: "Edit filter",
  groupValuesText: "Values",
  groupPropertiesText: "Properties",
  operatorsText: "Operators",
  operationAndText: "and",
  operationOrText: "or",
  operatorLessText: "Less than",
  operatorLessOrEqualText: "Less than or equal",
  operatorGreaterText: "Greater than",
  operatorGreaterOrEqualText: "Greater than or equal",
  operatorContainsText: "Contains",
  operatorDoesNotContainText: "Does not contain",
  operatorEqualsText: "Equal",
  operatorDoesNotEqualText: "Does not equal",
  operatorStartsWithText: "Starts with",
  operatorDoesNotStartWithText: "Does not starts with",
  operatorText: "Operator",
  propertyText: "Property",
  valueText: "Value",
  applyActionText: "Apply",
  allPropertiesLabel: "",
  clearAriaLabel: "Clear",
  tokenLimitShowMore: "Show more",
  tokenLimitShowFewer: "Show fewer",
  clearFiltersText: "Clear filters",
  tokenOperatorAriaLabel: "Boolean Operator",
  enteredTextLabel: (text: string) => `Use: "${text}"`,
  formatToken,
  removeTokenButtonAriaLabel: (token) => `Remove token, ${formatToken(token)}`,
  tokenEditorAddExistingTokenAriaLabel: (token) =>
    `Add filter ${formatToken(token)} to group`,
  tokenEditorAddExistingTokenLabel: (token) =>
    `Add filter ${getTokenLabel(token)} to group`,
  tokenEditorTokenActionsAriaLabel: (token) =>
    `Filter remove actions for ${formatToken(token)}`,
  tokenEditorTokenRemoveAriaLabel: (token) =>
    `Remove filter, ${formatToken(token)}`,
  tokenEditorTokenRemoveLabel: "Remove filter",
  tokenEditorTokenRemoveFromGroupLabel: "Remove filter from group",
  tokenEditorAddNewTokenLabel: "Add new filter",
  tokenEditorAddTokenActionsAriaLabel: "Add filter actions",
  groupEditAriaLabel: (group) =>
    `Edit group with ${group.tokens.length} tokens`,
};

function formatToken(token: PropertyFilterProps.FormattedToken) {
  return `${token.propertyLabel} ${operatorToLabel(token.operator)} ${
    token.value
  }`;
}

function getTokenLabel(token: PropertyFilterProps.FormattedToken) {
  return `${token.propertyLabel} ${token.operator} ${token.value}`;
}

function operatorToLabel(operator: string) {
  switch (operator) {
    case "=":
      return PROPERTY_FILTER_I18N.operatorEqualsText;
    case "!=":
      return PROPERTY_FILTER_I18N.operatorDoesNotEqualText;
    case ">":
      return PROPERTY_FILTER_I18N.operatorGreaterText;
    case ">=":
      return PROPERTY_FILTER_I18N.operatorGreaterOrEqualText;
    case "<":
      return PROPERTY_FILTER_I18N.operatorLessText;
    case "<=":
      return PROPERTY_FILTER_I18N.operatorLessOrEqualText;
    case ":":
      return PROPERTY_FILTER_I18N.operatorContainsText;
    case "!:":
      return PROPERTY_FILTER_I18N.operatorDoesNotContainText;
    case "^":
      return PROPERTY_FILTER_I18N.operatorStartsWithText;
    case "!^":
      return PROPERTY_FILTER_I18N.operatorDoesNotStartWithText;
    default:
      return operator;
  }
}
